import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';

import { routes } from '../../utils/routes';
import internalLinkIcon from './icons/arrow-black-right.svg';
import externalLinkIcon from './icons/outside-link-icon.svg';
import houseIcon from './icons/house.svg';
import skiIcon from './icons/skis.svg';
import bedIcon from './icons/bed.svg';
import slopeIcon from './icons/slopes.png';
import hausviseIcon from './icons/hausvise.svg';
import mountainIcon from './icons/mountain_cabin.png';
import calendarIcon from './icons/calendar.svg';
import { getDomain, getDrupalLink } from '../../utils/functions';
import { Customer } from '../../types/customer';
import seasonIcon from './icons/season.svg';

interface MenuItem {
  title: string;
  icon: string;
  route: string;
  external?: boolean;
  onClick?: void;
}

interface Props {
  customer?: Customer;
}

function IconMenu(props: Props): JSX.Element {
  const { t, i18n } = useTranslation(['menu', 'common']);
  const { accounts } = useMsal();
  const isOwner = accounts[0]?.idTokenClaims?.jobTitle === 'owner';
  const isLoyaltyMember = props.customer
    ? props.customer.loyaltyMembershipStatus === 'Active'
    : false;

  const languageIndex: number = i18n.language === 'fi' ? 0 : 1;
  const accommodationSearchUrl: { [key: string]: string } = {};
  accommodationSearchUrl['ruka'] =
    process.env.REACT_APP_ACCOMMODATION_SEARCH_RUKA?.split(',')[
      languageIndex
    ] ?? '';
  accommodationSearchUrl['pyha'] =
    process.env.REACT_APP_ACCOMMODATION_SEARCH_PYHA?.split(',')[
      languageIndex
    ] ?? '';

  let menuItems: MenuItem[];
  if (isOwner) {
    menuItems = [
      {
        title: t('Own apartment'),
        icon: calendarIcon,
        route: routes.ROOMS[i18n.language],
      },
      {
        title: t('My Ski-Inn reservations'),
        icon: houseIcon,
        route: routes.BOOKINGS[i18n.language],
      },
      {
        title: t('Quotas'),
        icon: mountainIcon,
        route: routes.QUOTAS[i18n.language],
      },
      {
        title: t('Purchase history'),
        icon: skiIcon,
        route: routes.ORDER_HISTORY[i18n.language],
      },
      {
        title: t('Ski-days'),
        icon: slopeIcon,
        route: routes.SKIDAYS[i18n.language],
      },
      {
        title: 'Hausvise',
        icon: hausviseIcon,
        route: routes.HAUSVISE[i18n.language],
        external: true,
      },
    ];
  } else {
    menuItems = [
      {
        title: t('Make a new Ski-Inn booking'),
        icon: bedIcon,
        route: getDrupalLink(accommodationSearchUrl[getDomain()]),
        external: true,
      },
      {
        title: t('My Ski-Inn reservations'),
        icon: houseIcon,
        route: routes.BOOKINGS[i18n.language],
      },

      {
        title: t('Purchase history'),
        icon: skiIcon,
        route: routes.ORDER_HISTORY[i18n.language],
      },
      {
        title: t('Ski-days'),
        icon: slopeIcon,
        route: routes.SKIDAYS[i18n.language],
      },
    ];

    if (isLoyaltyMember) {
      menuItems.push({
        title: t('Benefits'),
        icon: seasonIcon,
        route: routes.BENEFITS[i18n.language],
      });
    }
  }

  const linkIconClasses = 'ml-auto h-3 md:hidden';
  // Makes the menuitems get wrapped on two rows, when on narrow but not mobile screen.
  const itemFlexBasis = menuItems.length > 5 ? 'basis-1/5' : '';
  return (
    <ul className='flex md:flex-wrap lg:flex-nowrap flex-col px-7 md:flex-row md:justify-center md:mb-5'>
      {menuItems.map(
        (menuItem, i) =>
          menuItem && (
            <li
              className={`flex md:flex-col items-center my-5 md:mb-10 md:mx-6 lg:mx-10 md:justify-center relative ${itemFlexBasis}`}
              key={i}
            >
              <span className='md:bg-white md:rounded-full md:relative md:p-10 md:flex md:items-center w-8 md:w-auto'>
                <img
                  className='md:absolute md:right-0 md:left-0 md:mx-auto h-auto w-5 md:w-10'
                  src={menuItem.icon}
                  alt={`${menuItem.title} ${t('icon', { ns: 'common' })}`}
                />
              </span>
              <p className='uppercase ml-3 md:ml-0 font-rukaNeue text-sm text-left md:text-center md:w-[10rem] md:absolute md:top-20'>
                {menuItem.title}
              </p>
              {menuItem.external ? (
                <img
                  className={linkIconClasses}
                  src={externalLinkIcon}
                  alt={t('External link', { ns: 'common' }) ?? ''}
                />
              ) : (
                <img
                  className={linkIconClasses}
                  src={internalLinkIcon}
                  alt={t('Internal link', { ns: 'common' }) ?? ''}
                />
              )}
              <a
                onClick={menuItem.onClick ?? undefined}
                href={
                  menuItem.external
                    ? menuItem.route
                    : `${i18n.language}/${menuItem.route}`
                }
                className='absolute h-full w-full z-10 no-underline'
                target={menuItem.external ? '_blank' : ''}
                rel={menuItem.external ? 'noreferrer' : ''}
              >
                &nbsp;
              </a>
            </li>
          )
      )}
    </ul>
  );
}

export default IconMenu;
